/*
 * @providesModule AvatarView
 *
 */

import ClientPropertyConstants from 'ClientPropertyConstants';
import Optional from 'Optional';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';
import {SKZUserInterface} from 'SKZUser';
import React from 'react';
import {ImageStyle, StyleSheet, View, ViewStyle} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';

const {btnTextColorOne, textColorSecondary} = SkillzStyle.fullStyles;

export const kDefaultAvatarImageSizeHighRes = 200;
const kDefaultAvatarImageSize = 50;
const ONLINE_INDICATOR_SIZE_RATIO = 0.1;
const ONLINE_INDICATOR_MINIMUM_SIZE = 8;
const LOYALTY_TIER_MAXIMUM_LEVEL = 6;

enum TierName {
  Bronze = 1,
  Silver,
  Gold,
  Platinum,
  Diamond,
  Black_Diamond,
}

const tierAvatarFrameGradientColors = {
  1: ['#93A2B2', '#F2F2F2'],
  2: ['#048EE5', '#65DFFD'],
  3: ['#0FC76C', '#1BDDAA'],
  4: ['#976135', '#F3E45C'],
  5: ['#E42955', '#EF878C'],
  6: ['#3236B0', '#4161FF'],
};

export const getTierImage = (multiplier: number | undefined): string => {
  if (!multiplier || multiplier > LOYALTY_TIER_MAXIMUM_LEVEL) {
    return '';
  }
  return TierName[multiplier] + '.png';
};

interface IAvatarProps {
  avatarURL?: string;
  imageCornerRadius?: number;
  tier?: number;
  backgroundCornerRadius?: number;
  borderWidth?: number;
  backgroundColor?: string;
  avatarColor?: string;
  style?: ViewStyle;
  isOnline?: boolean;
  testID?: string;
  useHighResAvatar?: boolean;
  tierDetails: SKZUserInterface['ticketz'];
  paddingExtra: number;
  hideavatar?: boolean;
  tierBadgeStyle?: ImageStyle;
}

const AvatarView = ({
  avatarURL = '',
  imageCornerRadius = 0,
  backgroundCornerRadius = 0,
  borderWidth = 1,
  isOnline = false,
  testID = 'avatar_view_test_id',
  useHighResAvatar = false,
  backgroundColor = 'transparent',
  avatarColor,
  style,
  tier,
  tierDetails,
  hideavatar = false,
  paddingExtra = 0,
  tierBadgeStyle,
}: IAvatarProps) => {
  let viewStyle = StyleSheet.flatten(style);
  viewStyle = viewStyle || {};

  const size: {width: number; height: number} = {
    width:
      (viewStyle.width as number) ||
      (useHighResAvatar ? kDefaultAvatarImageSizeHighRes : kDefaultAvatarImageSize),
    height:
      (viewStyle.height as number) ||
      (useHighResAvatar ? kDefaultAvatarImageSizeHighRes : kDefaultAvatarImageSize),
  };

  const styleForImageWithStyle = (): ImageStyle => {
    const width = size.width + (paddingExtra > 0 ? -paddingExtra : 0);
    const height = size.height + (paddingExtra > 0 ? -paddingExtra : 0);
    const borderRadius = ClientPropertyConstants.getLoyaltyProfileFramesEnabled()
      ? size.width / 2
      : imageCornerRadius;
    return {
      borderRadius: borderRadius,
      height: width,
      width: height,
    };
  };

  const styleForContainerWithStyle = (): ViewStyle => {
    const width = size.width + (paddingExtra ? -paddingExtra : 0);
    const cornerRadius = ClientPropertyConstants.getLoyaltyProfileFramesEnabled()
      ? width / 2
      : backgroundCornerRadius;
    const height = size.height + (paddingExtra ? -paddingExtra : 0);
    return {
      width,
      height,
      backgroundColor: backgroundColor,
      borderRadius: cornerRadius,
      ...viewStyle,
    };
  };

  const styleForOnlineIndicator = (): ViewStyle => {
    let width = size.width * ONLINE_INDICATOR_SIZE_RATIO;
    if (width < ONLINE_INDICATOR_MINIMUM_SIZE) {
      width = ONLINE_INDICATOR_MINIMUM_SIZE;
    }
    let height = size.height * ONLINE_INDICATOR_SIZE_RATIO;
    if (height < ONLINE_INDICATOR_MINIMUM_SIZE) {
      height = ONLINE_INDICATOR_MINIMUM_SIZE;
    }

    return StyleSheet.flatten([
      styles.onlineIndicator,
      {
        width: width,
        height: height,
        borderRadius: width / 2,
      },
    ]);
  };

  const styleForAvatarFrameWithStyle = (): ImageStyle => {
    return {
      width: size.width,
      height: size.height,
      position: 'absolute',
      left: 0,
      top: 0,
    };
  };

  const renderOnlineIndicator = () => {
    if (!isOnline) {
      return null;
    }

    let indicatorStyle = StyleSheet.flatten(style);
    indicatorStyle = indicatorStyle || {};

    return <View style={styleForOnlineIndicator()} />;
  };

  const imageURI = avatarURL || 'skillz-generic-avatar';
  if (imageURI === 'skillz-generic-avatar') {
    return (
      <View
        style={[
          styleForContainerWithStyle(),
          styles.genericAvatarContainer,
          {
            borderRadius: ClientPropertyConstants.getLoyaltyProfileFramesEnabled()
              ? size.width / 2
              : imageCornerRadius,
          },
        ]}
        testID={testID}
      >
        <SkillzImage
          source={'avatar_pending_generic_skz.png'}
          style={styleForImageWithStyle()}
          color={btnTextColorOne.color}
          resizeMode={'contain'}
          key={imageURI + 'generic'}
        />
        {renderOnlineIndicator()}
      </View>
    );
  } else {
    const colorCodeArray = tierAvatarFrameGradientColors[tierDetails?.ticketz_tier_id];
    const isNewLoyaltyRevampEnabled =
      colorCodeArray && ClientPropertyConstants.getLoyaltyRevampUIV2Enabled();
    return (
      <View style={[styleForContainerWithStyle()]} testID={testID}>
        {isNewLoyaltyRevampEnabled ? (
          <LinearGradient
            colors={colorCodeArray}
            style={[
              styles.linearGradient,
              {
                width: size.width + paddingExtra,
                height: size.height + paddingExtra,
              },
            ]}
          >
            <SkillzImage
              source={imageURI}
              resizeMode={'cover'}
              style={[styleForImageWithStyle()]}
              color={avatarColor}
              key={imageURI}
            />
          </LinearGradient>
        ) : (
          <SkillzImage
            source={imageURI}
            resizeMode={'cover'}
            style={[styleForImageWithStyle()]}
            color={avatarColor}
            key={imageURI}
          />
        )}
        <Optional test={ClientPropertyConstants.getLoyaltyRevampUIV2Enabled()}>
          {hideavatar ? null : (
            <SkillzImage
              source={tierDetails?.scalable_image}
              resizeMode={'contain'}
              style={[styles.tierBadge, tierBadgeStyle]}
            />
          )}
        </Optional>
        {renderOnlineIndicator()}
      </View>
    );
  }
};

const styles = StyleSheet.create({
  onlineIndicator: {
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundColor: '#90CD49',
  },
  genericAvatarContainer: {
    backgroundColor: textColorSecondary.color,
  },
  linearGradient: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 50,
    elevation: 5,
  },
  tierBadge: {
    width: 20,
    height: 20,
    position: 'absolute',
    borderRadius: 10,
    right: -10,
    bottom: -5,
    zIndex: 100,
  },
});

export default AvatarView;
